import { fbt } from 'fbtee'
import { ChevronRight, Users } from 'lucide-react'
import invariant from 'tiny-invariant'
import { clickableProps } from '../lib/a11y'
import { formatDayShort } from '../lib/formatDate'
import { memberCountLabel } from '../lib/commonStrings'
import type { Event, Group, UserProfile } from '../types'
import { ThumbStack } from './profile/ThumbStack'
import { GroupFaceStack } from './GroupFaceStack'
import { Skeleton } from './ui/skeleton'

/**
 * One group in the Crew tab: square photo, name, its next linked event and how
 * many members are going, and a few members' faces (those going first).
 */
export function CrewGroupRow({
  loading,
  group,
  nextEvent,
  goingCount = 0,
  goingUserIds,
  profiles = {},
  locale = 'en_US',
  onClick,
}: {
  loading?: boolean
  group?: Group
  nextEvent?: Event
  goingCount?: number
  goingUserIds?: Set<string>
  profiles?: Record<string, UserProfile>
  locale?: string
  onClick?: () => void
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-3 px-2 py-2.5">
        <Skeleton className="size-10 shrink-0 rounded-md" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3.5 w-2/5" />
          <Skeleton className="h-3 w-3/5" />
        </div>
      </div>
    )
  }

  invariant(group, 'CrewGroupRow: group is required when not loading')

  return (
    <div
      {...clickableProps(onClick)}
      className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-muted"
      data-testid="crew-group-row"
    >
      <ThumbStack srcs={[group.imageUrl]} icon={Users} size="md" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{group.name}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {nextEvent
            ? goingCount > 0
              ? fbt(
                  fbt.param('event', nextEvent.name) + ' · ' +
                    fbt.param('date', formatDayShort(nextEvent.startDate.slice(0, 10), locale)) + ' · ' +
                    fbt.plural('going', goingCount, { showCount: 'yes', many: 'going' }),
                  'Crew group row subtitle: the next linked event, its date and how many members are going',
                )
              : fbt(
                  fbt.param('event', nextEvent.name) + ' · ' +
                    fbt.param('date', formatDayShort(nextEvent.startDate.slice(0, 10), locale)),
                  'Crew group row subtitle: the next linked event and its date',
                )
            : memberCountLabel(group.memberIds.length)}
        </span>
      </span>
      <GroupFaceStack
        memberIds={group.memberIds}
        first={goingUserIds}
        profiles={profiles}
        dialogTitle={group.name}
        historyKey={`crew-group:${group.id}`}
      />
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </div>
  )
}
